import React, { useState, useEffect, useRef } from 'react';
import {
  FaCar,
  FaRoute,
  FaTachometerAlt,
  FaClock,
  FaCommentAlt,
  FaLock,
  FaUnlock,
} from 'react-icons/fa';

const DrivingSessionSidebar = ({
  isExpanded,
  setIsExpanded,
  comments,
  currentTime,
  onCommentClick,
  duration,
  distance,
  averageSpeed,
}) => {
  const sidebarRef = useRef(null);
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    const handleMouseMove = e => {
      if (isLocked) return;
      const screenWidth = window.innerWidth;
      const mouseX = e.clientX;
      const threshold = screenWidth - 50;
      if (mouseX > threshold) {
        setIsExpanded(true);
      } else {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [setIsExpanded, isLocked]);

  const toggleLock = () => {
    setIsLocked(!isLocked);
    if (!isLocked) {
      setIsExpanded(true);
    }
  };

  const formatTime = seconds => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours > 0 ? hours + 'h ' : ''}${mins}m ${secs}s`;
  };

  return (
    <div
      ref={sidebarRef}
      className={`fixed top-0 right-0 h-full bg-white shadow-lg transition-all duration-300 ease-in-out ${
        isExpanded ? 'w-96' : 'w-16'
      } overflow-hidden z-10`}
      aria-label="Driving Session Summary"
      role="complementary"
    >
      <div className="p-4 h-full overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center">
            <FaCar className="mr-2" />
            <span className={isExpanded ? '' : 'hidden'}>
              Informe conducción
            </span>
          </h2>
          {isExpanded && (
            <button
              onClick={toggleLock}
              className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              aria-label={isLocked ? 'Unlock sidebar' : 'Lock sidebar'}
            >
              {isLocked ? <FaLock /> : <FaUnlock />}
            </button>
          )}
        </div>

        <div className={`space-y-6 ${isExpanded ? '' : 'hidden'}`}>
          <div className="flex items-center">
            <FaClock className="text-blue-500 mr-2" />
            <span className="font-semibold">Duración:</span>
            <span className="ml-2">{formatTime(duration)}</span>
          </div>

          <div className="flex items-center">
            <FaRoute className="text-green-500 mr-2" />
            <span className="font-semibold">Distancia:</span>
            <span className="ml-2">{distance.toFixed(2)} km</span>
          </div>

          <div className="flex items-center">
            <FaTachometerAlt className="text-red-500 mr-2" />
            <span className="font-semibold">Velocidad media:</span>
            <span className="ml-2">{averageSpeed.toFixed(2)} km/h</span>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2 flex items-center">
              <FaCommentAlt className="text-yellow-500 mr-2" />
              Comentarios:
            </h3>
            <ul className="space-y-2">
              {comments.map(comment => (
                <li
                  key={comment.id}
                  className={`p-2 rounded cursor-pointer ${
                    comment.timestamp <= currentTime
                      ? 'bg-gray-100'
                      : 'bg-white'
                  }`}
                  onClick={() => onCommentClick(comment.timestamp)}
                >
                  <p
                    className={`font-semibold ${comment.type === 'instructor' ? 'text-green-600' : 'text-yellow-600'}`}
                  >
                    {comment.type === 'instructor' ? 'Instructor' : 'AI'} -{' '}
                    {formatTime(comment.timestamp)}
                  </p>
                  <p className="text-gray-700">{comment.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {!isExpanded && (
          <div className="flex flex-col items-center space-y-4">
            <FaClock className="text-blue-500" />
            <FaRoute className="text-green-500" />
            <FaTachometerAlt className="text-red-500" />
            <FaCommentAlt className="text-yellow-500" />
          </div>
        )}
      </div>
    </div>
  );
};

export default DrivingSessionSidebar;
